import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Sparkles,
  ShieldCheck,
  Store,
  Users,
  MessageSquare,
  Heart,
  MapPin,
  Crown,
  ArrowRight,
  Info,
} from "lucide-react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useProfile } from "@/lib/profile/useProfile";
import { SIBLING_APPS, getSiblingApp } from "@/lib/sibling-apps";
import { supabase } from "@/integrations/supabase/client";

const FEATURES = [
  {
    Icon: Crown,
    title: "Attuned tier unlocked",
    desc: "You have full top-tier access across all 9 Wavlynq apps — every feature, no limits.",
  },
  {
    Icon: Users,
    title: "Discovery feed",
    desc: "Browse demo profiles in the discovery feed. These are seed profiles — real members aren't here yet.",
  },
  {
    Icon: MessageSquare,
    title: "Messaging & RAELA",
    desc: "Send messages, try voice notes, and chat with RAELA — our AI companion for safety and matchmaking.",
  },
  {
    Icon: Store,
    title: "Wavlynq Store",
    desc: "Browse curated products from 9 community vendors. Real checkout is disabled in sandbox mode.",
  },
  {
    Icon: Heart,
    title: "All à la carte features",
    desc: "AMPLIFY, Priority Matching, Advanced Analytics, ROAM, and every add-on — all active.",
  },
  {
    Icon: ShieldCheck,
    title: "Safety tools",
    desc: "Emergency Button, Crisis Resources, Pre-Meet Checklists, Trusted Circle — all live and functional.",
  },
  {
    Icon: MapPin,
    title: "Events & meetups",
    desc: "Create and browse community events. RSVP, check-in, and test the full event flow.",
  },
];

const SANDBOX_NOTES = [
  "This is a sandbox environment with demo data — not real members.",
  "Your account is persistent. You can log back in anytime.",
  "Profiles you see are seed profiles to demonstrate the experience.",
  "Stripe checkout is live but sandbox products won't charge real cards.",
  "Your feedback matters — reach out to partners@wavlynq.com anytime.",
];

const SandboxWelcome = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { profile } = useProfile();
  const [step, setStep] = useState(0);
  const [chosenApp, setChosenApp] = useState<string | null>(null);

  const appId = chosenApp;
  const app = appId ? getSiblingApp(appId) : null;
  const appName = app?.name ?? "Wavlynq";
  const community = app?.community ?? "all communities";

  useEffect(() => {
    if (!session) navigate("/partner-access", { replace: true });
  }, [session, navigate]);

  const handleChooseApp = async (id: string) => {
    setChosenApp(id);
    // Persist to profile
    if (session?.user?.id) {
      await supabase.from("profiles").update({
        primary_app: id as any,
        active_app: id as any,
      }).eq("id", session.user.id);
    }
  };

  const handleEnter = () => {
    if (appId) {
      navigate(`/${appId}`, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  // If no app is set, show app chooser as step 0
  const needsAppChoice = !chosenApp;

  if (needsAppChoice && step === 0) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-3xl w-full text-center space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              Sandbox Environment
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Welcome to Wavlynq
            </h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              Choose the community you'd like to explore first. You can switch
              between all 9 apps anytime from the header menu.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {SIBLING_APPS.map((a) => (
              <button
                key={a.id}
                onClick={() => handleChooseApp(a.id)}
                className="group rounded-xl border border-border/60 bg-card/50 p-4 text-left transition-all hover:border-primary/40 hover:bg-card focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-semibold text-sm">{a.name}</span>
                </div>
                <p className="text-xs text-muted-foreground">{a.community}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (step === 0) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-2xl w-full text-center space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              Sandbox Environment
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Welcome to {appName}
            </h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              You're entering a sandbox preview of the Wavlynq ecosystem —
              specifically the <span className="text-foreground font-medium">{community}</span> community.
            </p>
          </div>

          <Card className="p-6 text-left space-y-4 bg-card/50 border-primary/20">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold">What is this?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This is a <strong>fully functional preview</strong> of what Wavlynq members experience.
                  You have the highest tier (Attuned) with every feature unlocked — discovery, messaging,
                  events, the store, safety tools, AI companion (RAELA), and all à la carte add-ons.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold">Demo profiles, not real people</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  The profiles in discovery are seed data representing the kind of members each
                  community attracts. You can browse, match, and message — but there's no one on the
                  other end (yet). This is about experiencing the product, not meeting people.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold">Your account is persistent</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This isn't a time-limited demo. Your account stays active — come back anytime
                  to explore. Bookmark any app page and pick up where you left off.
                </p>
              </div>
            </div>
          </Card>

          <Button size="lg" onClick={() => setStep(1)} className="gap-2 font-semibold text-base px-8">
            See what's included <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  if (step === 1) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-3xl w-full space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-bold tracking-tight">Everything's unlocked</h2>
            <p className="text-muted-foreground">
              Here's what you can explore in your sandbox:
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {FEATURES.map(({ Icon, title, desc }) => (
              <Card key={title} className="p-4 flex items-start gap-3">
                <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm">{title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center space-y-2">
            <p className="text-xs text-muted-foreground">
              All 9 apps:{" "}
              {SIBLING_APPS.map((a, i) => (
                <span key={a.id}>
                  {i > 0 && " · "}
                  <span className={a.id === appId ? "text-foreground font-medium" : ""}>
                    {a.name}
                  </span>
                </span>
              ))}
            </p>
          </div>

          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => setStep(0)}>
              Back
            </Button>
            <Button size="lg" onClick={() => setStep(2)} className="gap-2 font-semibold">
              Got it — one more thing <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: final notes + enter
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="max-w-2xl w-full text-center space-y-8">
        <div className="space-y-3">
          <h2 className="text-3xl font-bold tracking-tight">Before you dive in</h2>
          <p className="text-muted-foreground">A few things to keep in mind:</p>
        </div>

        <Card className="p-6 text-left space-y-3">
          {SANDBOX_NOTES.map((note, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="text-xs font-bold text-primary bg-primary/10 rounded-full h-5 w-5 flex items-center justify-center shrink-0 mt-0.5">
                {i + 1}
              </span>
              <p className="text-sm text-muted-foreground">{note}</p>
            </div>
          ))}
        </Card>

        <div className="space-y-3">
          <Button size="lg" onClick={handleEnter} className="gap-2 font-semibold text-base px-10">
            Enter {appName} <Sparkles className="h-4 w-4" />
          </Button>
          <p className="text-xs text-muted-foreground">
            You can switch apps anytime from the header menu.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SandboxWelcome;
